import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { apiService } from '../services/api';
import { User } from '../types';

interface AuthContextValue {
  user: User | null;
  loading: boolean;
  // `ticket` is left over from the old sign-in queue. The server ignores
  // it now: there is no ticket step any more.
  login: (email: string, password: string, ticket: string) => Promise<User>;
  logout: () => void;
  // Patches the cached user in-place (e.g. after uploading a profile
  // picture) so components reading it via useAuth() — the Sidebar's
  // avatar, in particular — update immediately without a full reload.
  updateUser: (user: User) => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!apiService.getToken()) {
      setLoading(false);
      return;
    }

    apiService
      .getCurrentUser()
      .then(setUser)
      .catch(() => apiService.clearToken())
      .finally(() => setLoading(false));
  }, []);

  const login = async (email: string, password: string, ticket: string) => {
    const response = await apiService.login(email, password, ticket);
    setUser(response.user);
    return response.user;
  };

  const logout = () => {
    apiService.clearToken();
    setUser(null);
  };

  return <AuthContext.Provider value={{ user, loading, login, logout, updateUser: setUser }}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
